import { Component, OnInit, NgZone } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Category } from '../models/category';
import { Feedback } from '../models/feedback';
import { BannerService } from '../osm-services/banner.service';
import { CategoryService } from '../osm-services/category.service';
import { AppUsageService, MostUsedApp } from '../osm-services/app-usage.service';
import { fallIn, moveIn, moveInLeft } from '../router.animations';
import Swiper, { SwiperOptions } from 'swiper';
import { AuthService } from '../services/auth.service';

interface AppInCategory {
  appliId: string;
  name: string;
  icon: string;
  androidImageUrl: string;
  active: boolean;
  order: number;
  category: string;
  subCategory: string;
  link: string;
  fallbackUrl: string;
  fallbackUrl2: string;
  packageName: string;
  openByPackageName: boolean;
  default: boolean;
  checked?: boolean;
}

interface CategoryWithApps {
  categoryId: string;
  name: string;
  icon: string;
  iconPath: string;
  active: boolean;
  order: number;
  apps: AppInCategory[];
}

@Component({
  selector: 'ozm-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  animations: [moveIn(), fallIn(), moveInLeft()],
  host: { '[@moveIn]': '' },
})
export class HomeComponent implements OnInit {
    private fragmentSubscription: any;
  feedbackFormGroup: any;
  feedback: Feedback = new Feedback();
  userdata: any;
  email: string = '';
  map = {};

  // Selected category for main content display
  selectedCategory: CategoryWithApps | null = null;

  // Most used apps
  mostUsedApps: MostUsedApp[] = [];
  mostUsedLoading: boolean = false;

  config = new Swiper('.swiper', {
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
    navigation:{
      nextEl:'swiper-button-next',
      prevEl:'swiper-button-prev',
    },
    spaceBetween: 30,
    autoplay: {
      delay: 3000,
    },
  });


  constructor(
    private _authService: AuthService,
    private _router: Router,
    private bannerService: BannerService,
    private categoryService: CategoryService,
    private appUsageService: AppUsageService,
    private ngZone: NgZone
    // private dataservice: Dataserviceervice
  ) {}

  ngOnInit(): void {
    try {
    const idFromLocalStorage = localStorage.getItem('#id');
    if (idFromLocalStorage !== null) {
      window.location.hash = '';
      console.log('Setting hash to empty');
      console.log('Retrieved ID from localStorage:', idFromLocalStorage);
      window.location.hash = idFromLocalStorage;
      localStorage.removeItem('#id');
    } else {
      console.log('No ID found in localStorage');
    }
    } catch (e) {
      console.error('Error occurred while setting hash:', e);
    }


    
    // if (idTokenFromLocalStorage !== null) {
    //   window.location.hash = '';
    //   window.location.hash = idTokenFromLocalStorage;
    //   // localStorage.removeItem('idTokenFromLocalStorage');
    // }

    this.createForm();

    this._authService.getUser().then((userDataRes) => {
      this.userdata = userDataRes;
      // Load most used apps after user data is loaded
      this.loadMostUsedApps();
    });

    // Listen for fragment changes and scroll to section
    this.fragmentSubscription = this._router.events.subscribe((event: any) => {
      if (event && event.constructor && event.constructor.name === 'NavigationEnd') {
        const fragment = this._router.parseUrl(this._router.url).fragment;
        if (fragment) {
          // Clear sidebar selection when navigating to a header section
          this.selectedCategory = null;
          
          this.ngZone.runOutsideAngular(() => {
            // Try scrolling multiple times in case the element is not yet rendered
            let attempts = 0;
            const maxAttempts = 20;
            const scrollToFragment = () => {
              const el = document.getElementById(fragment);
              if (el) {
                try {
                  // Force a reflow to ensure element is fully rendered
                  el.offsetHeight;
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } catch (e) {
                  try {
                    el.scrollIntoView();
                  } catch (e2) {
                    console.error('Scroll failed:', e2);
                  }
                }
              } else if (attempts < maxAttempts) {
                attempts++;
                setTimeout(scrollToFragment, 150);
              } else {
                console.warn('Could not find element with id:', fragment);
              }
            };
            // Add initial delay for production environments
            setTimeout(scrollToFragment, 300);
          });
        }
      }
    });
  }

  createForm() {
    this.feedbackFormGroup = new FormGroup({
      feedbackType: new FormControl(this.feedback.feedbackType, []),
      reviewComment: new FormControl(""),
      reviewed: new FormControl(false, [])
    });
  }

  ngAfterViewInit() {
    // Always scroll to fragment after view is initialized
    this.ngZone.runOutsideAngular(() => {
      let attempts = 0;
      const maxAttempts = 20;
      const fragment = this._router.parseUrl(this._router.url).fragment;
      const scrollToFragment = () => {
        if (fragment) {
          const el = document.getElementById(fragment);
          if (el) {
            try {
              // Force a reflow to ensure element is fully rendered
              el.offsetHeight;
              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } catch (e) {
              try {
                el.scrollIntoView();
              } catch (e2) {
                console.error('Scroll failed:', e2);
              }
            }
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(scrollToFragment, 150);
          } else {
            console.warn('Could not find element with id:', fragment);
          }
        }
      };
      // Add initial delay for production environments
      setTimeout(scrollToFragment, 400);
    });
  }

  ngOnDestroy() {
    if (this.fragmentSubscription) {
      this.fragmentSubscription.unsubscribe();
    }
  }

  onSubmit() {
    this.feedback.userName = '' ;
    this.feedback.phoneNumber = '';
    this.feedback.submitTimeStamp = new Date();
    this.feedback.uid = '';
  }

  // Sidebar interaction methods
  onCategorySelected(category: CategoryWithApps): void {
    this.selectedCategory = category;
    console.log('Category selected:', category);
    // Reset scroll position when category is selected from sidebar
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  getAppIconUrl(app: AppInCategory): string {
    return app.androidImageUrl || '/assets/images/default.png';
  }

  handleImageError(event: any): void {
    event.target.src = '/assets/images/default.png';
  }

  openApp(app: AppInCategory): void {
    // Get user ID if available
    const userId = this.userdata?.attributes?.sub || null;
    
    if (!userId) {
      console.warn('User ID not available for tracking');
      // Still open the app even if tracking fails
      this.openAppWindow(app);
      return;
    }
    
    // Track the usage
    this.appUsageService.trackAppUsage({
      userId: userId,
      appId: app.appliId,
      appName: app.name,
      category: app.category,
      subCategory: app.subCategory
    }).subscribe({
      next: (response) => {
        console.log('App usage tracked:', response);
      },
      error: (error) => {
        console.error('Error tracking app usage:', error);
      }
    });

    // Open the app
    this.openAppWindow(app);
  }

  private openAppWindow(app: AppInCategory): void {
    let targetUrl = app.link;
    
    if (!targetUrl && app.fallbackUrl) {
      targetUrl = app.fallbackUrl;
    }
    
    if (!targetUrl && app.fallbackUrl2) {
      targetUrl = app.fallbackUrl2;
    }
    
    if (targetUrl) {
      window.open(targetUrl, '_blank');
    } else {
      console.warn('No valid URL found for app:', app.name);
    }
  }

  /**
   * Load most used apps from usage tracking
   */
  loadMostUsedApps(): void {
    this.mostUsedLoading = true;
    
    const userId = this.userdata?.attributes?.sub;
    if (!userId) {
      console.warn('User ID not available for loading most used apps');
      this.mostUsedApps = [];
      this.mostUsedLoading = false;
      return;
    }
    
    this.appUsageService.getMostUsedApps(userId, 3).subscribe({
      next: (response) => {
        if (response.mostUsedApps && response.mostUsedApps.length > 0) {
          this.mostUsedApps = response.mostUsedApps;
        } else {
          // Fallback to default apps if no data
          this.mostUsedApps = [];
        }
        this.mostUsedLoading = false;
      },
      error: (error) => {
        console.error('Error loading most used apps:', error);
        this.mostUsedApps = [];
        this.mostUsedLoading = false;
      }
    });
  }

  /**
   * Get icon URL for most used app
   */
  getMostUsedAppIcon(app: MostUsedApp): string {
    return app.androidImageUrl || app.icon || '/assets/images/default.png';
  }

  /**
   * Open most used app
   */
  openMostUsedApp(app: MostUsedApp): void {
    const userId = this.userdata?.attributes?.sub || null;
    
    if (userId) {
      // Track the usage
      this.appUsageService.trackAppUsage({
        userId: userId,
        appId: app.appliId,
        appName: app.name,
        category: app.category,
        subCategory: app.subCategory
      }).subscribe({
        next: (response) => {
          console.log('App usage tracked:', response);
        },
        error: (error) => {
          console.error('Error tracking app usage:', error);
        }
      });
    }

    // Open the app
    const targetUrl = app.link;
    
    if (targetUrl) {
      window.open(targetUrl, '_blank');
    } else {
      console.warn('No valid URL found for app:', app.name);
    }
  }
}
